/**
 * Carries "what the visitor was about to do" through the sign-up flow.
 *
 * CTAs that need an account (publish a job, buy credits, start the test) open the auth modal
 * and resume via an in-page `watch(isLoggedIn, ...)`. That works for someone who already has
 * an account — the modal closes and the page is still mounted. A brand-new Google/Facebook
 * sign-up is different: `AuthForm.moveNext()` routes them to /auth/registration first, the
 * page unmounts, and the intent is lost — they finish registration on /profile/welcome instead
 * of publishing their job.
 *
 * `beforeLogInLocation` is the one channel that survives registration (moveNext passes it on as
 * `?redirect=`), so we store the current page plus an `action` marker and replay it on arrival.
 */
const BEFORE_LOGIN_LOCATION_KEY = 'beforeLogInLocation'
const ACTION_QUERY_KEY = 'action'

/**
 * `replay: false` only records the intent. Use it when a different component on the same page
 * mounts later and should be the one to replay it (the SSR test landing hands off to the test form).
 */
export function usePendingAuthAction(
    action: string,
    resume: () => void | Promise<void>,
    { replay = true }: { replay?: boolean } = {}
) {
    const route = useRoute()
    const router = useRouter()

    /** Call right before opening the auth modal. */
    function remember() {
        if (!import.meta.client) return

        const [path, search = ''] = route.fullPath.split('?')
        const params = new URLSearchParams(search)
        params.set(ACTION_QUERY_KEY, action)
        localStorage.setItem(BEFORE_LOGIN_LOCATION_KEY, `${path}?${params.toString()}`)
    }

    onMounted(async () => {
        if (!replay || route.query[ACTION_QUERY_KEY] !== action) return

        // Strip the marker first so a refresh (or a second mount) doesn't replay the action.
        const query = { ...route.query }
        delete query[ACTION_QUERY_KEY]
        await router.replace({ query })

        await resume()
    })

    return { remember }
}
